// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import Head from 'next/head';
import { getAPIConfig, isClient, removeExtension, removeImageExtension } from '@tcp/core/src/utils';

export const isAbsoluteUrl = (url, isProductData) => !isProductData && /^http/i.test(url);

export const getAssetHostVal = (url, hostFromEnv) => {
  let hostValue = hostFromEnv;
  if (isAbsoluteUrl(url) === true) {
    const delimiter = 'upload';
    const indexVal = url.indexOf(delimiter);
    hostValue = indexVal !== -1 ? url.slice(0, indexVal + delimiter.length) : hostFromEnv;
    return hostValue;
  }
  return hostValue;
};

export const getImageSource = (assetHost, crop, url, src) => {
  const assetHostVal = getAssetHostVal(src, assetHost);
  return `${assetHostVal}/${crop}/${url}`;
};

export const cleanUpUrl = (url) => {
  let cleanUrl = url;
  if (isAbsoluteUrl(url) === true) {
    const start = url.indexOf('upload') + 'upload'.length;
    const end = url.length;
    cleanUrl = url.slice(start, end);
  }
  return cleanUrl ? cleanUrl.replace(/^\/,|^\//, '') : '';
};

const getCustomParam = (path, crop, imageConfigWithoutDPR = '', isFromHeroProductTile = false) => {
  const isDprValueExists = path.includes('dpr_') || crop.includes('dpr_');
  const dprValue = isClient() && window && Math.floor(window.devicePixelRatio);
  const addWithoutDPR = path.includes('t_plp') || crop.includes('t_plp');
  if (isFromHeroProductTile && addWithoutDPR && !isDprValueExists) return `${crop},dpr_3`;
  if (imageConfigWithoutDPR && addWithoutDPR && !isDprValueExists)
    return `${crop},${imageConfigWithoutDPR}`;
  if (isDprValueExists) return null;

  if (dprValue === 2) return `${crop},dpr_1.5`;

  return dprValue ? `${crop},dpr_${dprValue}` : `${crop},dpr_1.5`;
};

export const getImageSourceStringV2 = (
  img,
  crop,
  imageConfigWithoutDPR = '',
  isFromHeroProductTile = false
) => {
  const { assetHostTCP: assetHost } = getAPIConfig();
  const path = cleanUpUrl(img);
  const customParam = getCustomParam(path, crop, imageConfigWithoutDPR, isFromHeroProductTile);
  const assetHostVal = getAssetHostVal(img, assetHost);

  if (customParam) {
    return [{ url: `${assetHostVal}/${customParam}/${path}` }];
  }

  return [{ url: `${assetHostVal}/${crop}/${path}` }];
};

export const getImageSourcesV2 = (
  imgPaths,
  cropParams,
  imageConfigWithoutDPR = '',
  isFromHeroProductTile = false
) => {
  const desktop = getImageSourceStringV2(
    imgPaths.desktop,
    cropParams.desktop,
    imageConfigWithoutDPR,
    isFromHeroProductTile
  );
  const tablet = getImageSourceStringV2(
    imgPaths.tablet,
    cropParams.tablet,
    imageConfigWithoutDPR,
    isFromHeroProductTile
  );
  const mobile = getImageSourceStringV2(
    imgPaths.mobile,
    cropParams.mobile,
    imageConfigWithoutDPR,
    isFromHeroProductTile
  );
  return {
    desktop,
    tablet,
    mobile,
  };
};

/* eslint-disable camelcase */
export const getVideoUrls = (videoData, isProductVideo = false, primaryBrand, isProductData) => {
  const {
    url = '',
    url_t = '',
    url_m = '',
    url_es,
    url_m_es,
    url_t_es,
    url_fr,
    url_m_fr,
    url_t_fr,
  } = videoData;
  let desktop = url;
  let tablet = url_t;
  let mobile = url_m;
  const apiConfig = getAPIConfig();
  const { language } = apiConfig;
  let { brandId } = apiConfig;
  if (primaryBrand) {
    brandId = primaryBrand;
  }

  if (language === 'es') {
    desktop = url_es || url;
    tablet = url_t_es || url_t;
    mobile = url_m_es || url_m;
  }

  if (language === 'fr') {
    desktop = url_fr || url;
    tablet = url_t_fr || url_t;
    mobile = url_m_fr || url_m;
  }

  if (isProductVideo) {
    const assetPrefix = apiConfig[`productAssetPath${brandId.toUpperCase()}`];
    desktop = `${assetPrefix}/${desktop}`;
    tablet = `${assetPrefix}/${tablet}`;
    mobile = `${assetPrefix}/${mobile}`;
  }

  const xs = mobile || tablet || desktop;
  const sm = tablet || desktop;
  const lg = desktop;

  return {
    desktop: isAbsoluteUrl(lg, isProductData) ? lg : removeExtension(cleanUpUrl(lg)),
    tablet: isAbsoluteUrl(sm, isProductData) ? sm : removeExtension(cleanUpUrl(sm)),
    mobile: isAbsoluteUrl(xs, isProductData) ? xs : removeExtension(cleanUpUrl(xs)),
  };
};

/* eslint-enable camelcase */

export const getPosterImage = (url) => {
  const { assetHostTCP } = getAPIConfig();
  const urlVal = isAbsoluteUrl(url) ? removeExtension(cleanUpUrl(url)) : removeExtension(url);
  const assetHostVal = isAbsoluteUrl(url) ? getAssetHostVal(url, assetHostTCP) : assetHostTCP;
  const extn = 'jpg';
  const filteredUrl = urlVal?.replace(/(\/f_mp4|f_mp4|\/f_gif|f_gif)/g, '');
  const videoAssetPrefix = assetHostVal.replace('/image/', '/video/');
  return `${videoAssetPrefix}/${filteredUrl}.${extn}`;
};

export const getVideoPosterPreload = (preloadImage, desktopPoster, mobilePoster) => {
  return preloadImage ? (
    <Head>
      {desktopPoster && (
        <link
          rel="preload"
          as="image"
          href={desktopPoster}
          media="(min-width: 768px)"
          key={desktopPoster}
        />
      )}
      {mobilePoster && (
        <link
          rel="preload"
          as="image"
          href={mobilePoster}
          media="(max-width: 767px)"
          key={mobilePoster}
        />
      )}
    </Head>
  ) : null;
};

export const getPosterUrls = (videoData) => {
  const { poster_url: posterUrl, poster_url_t: posterUrlT, poster_url_m: posterUrlM } = videoData;
  const xs = posterUrlM || posterUrlT || posterUrl;
  const sm = posterUrlT || posterUrl;
  const lg = posterUrl;
  return {
    desktop: isAbsoluteUrl(lg) ? removeImageExtension(lg) : removeImageExtension(cleanUpUrl(lg)),
    tablet: isAbsoluteUrl(sm) ? removeImageExtension(sm) : removeImageExtension(cleanUpUrl(sm)),
    mobile: isAbsoluteUrl(xs) ? removeImageExtension(xs) : removeImageExtension(cleanUpUrl(xs)),
  };
};

export const getCustomPosterImage = (url) => {
  const { assetHostTCP } = getAPIConfig();
  const urlVal = isAbsoluteUrl(url) ? cleanUpUrl(url) : url;
  const assetHostVal = isAbsoluteUrl(url) ? getAssetHostVal(url, assetHostTCP) : assetHostTCP;
  const extn = 'jpg';
  const filteredUrl = urlVal.replace(/(\/f_mp4|f_mp4|\/f_gif|f_gif)/g, '');
  return `${assetHostVal}/${filteredUrl}.${extn}`;
};

export const findPosterImage = (posterUrls, videoUrls, variation) => {
  return posterUrls && posterUrls[variation]
    ? getCustomPosterImage(posterUrls[variation])
    : getPosterImage(videoUrls[variation]);
};

export const getCustomPosterForCloudinary = (posterUrls, variation) => {
  return posterUrls && posterUrls[variation] && getCustomPosterImage(posterUrls[variation]);
};
